/**
 * The "Update available" card. Appears on its own when the launch check finds a
 * newer build the user hasn't already waved away, and drives the download +
 * install from there. Off-device it never shows (the store's check() reports
 * up-to-date), so this is inert on web/dev.
 */
import { useUpdateStore } from '@/store/update';
import { haptic } from '@/ui/haptics';

/** Bytes as a one-decimal MB number, for live download progress. */
function mbFrac(bytes: number): string {
  return (bytes / 1_000_000).toFixed(1);
}

export function UpdatePrompt() {
  const checkStatus = useUpdateStore((s) => s.checkStatus);
  const installStatus = useUpdateStore((s) => s.installStatus);
  const manifest = useUpdateStore((s) => s.manifest);
  const dismissedCode = useUpdateStore((s) => s.dismissedCode);
  const progress = useUpdateStore((s) => s.progress);
  const bytes = useUpdateStore((s) => s.bytes);
  const total = useUpdateStore((s) => s.total);
  const error = useUpdateStore((s) => s.error);
  const startUpdate = useUpdateStore((s) => s.startUpdate);
  const dismiss = useUpdateStore((s) => s.dismiss);

  const busy = installStatus === 'downloading' || installStatus === 'installing';
  const available =
    checkStatus === 'available' && manifest != null && manifest.versionCode !== dismissedCode;

  // Show once there's something to offer, and keep showing while we download /
  // install / report an error so the user isn't left wondering.
  if (!available && !busy && installStatus !== 'error') return null;
  if (!manifest) return null;

  const pct = Math.round(progress * 100);
  const bytesLine =
    total > 0 ? `${mbFrac(bytes)} / ${mbFrac(total)} MB · ${pct}%` : `${mbFrac(bytes)} MB`;

  return (
    <div className="acp-update-prompt" role="dialog" aria-modal="true" aria-label="Update available">
      <b>Update available — {manifest.versionName}</b>
      {manifest.notes && <p className="acp-note">{manifest.notes}</p>}

      {installStatus === 'downloading' && (
        <p className="acp-note">Downloading… {bytesLine}</p>
      )}
      {installStatus === 'installing' && (
        <p className="acp-note">Opening the installer… follow the on-screen prompts to finish.</p>
      )}
      {installStatus === 'error' && error && (
        <p className="acp-note" style={{ color: 'var(--red)' }}>{error}</p>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
        {!busy && (
          <button
            type="button"
            className="acp-btn"
            style={{ opacity: 0.8 }}
            onClick={() => { haptic('tick'); dismiss(); }}
          >
            Not now
          </button>
        )}
        <button
          type="button"
          className="acp-btn on"
          disabled={installStatus === 'downloading' || installStatus === 'installing'}
          onClick={() => { haptic('tick'); void startUpdate(); }}
        >
          {installStatus === 'downloading'
            ? bytesLine
            : installStatus === 'installing'
              ? 'Installing…'
              : installStatus === 'error'
                ? 'Try again'
                : 'Update'}
        </button>
      </div>
    </div>
  );
}
